import React from 'react';

export interface ColumnDef {
  value: string;
  label: string;
  display: boolean;
  width?: number;
  flexGrow?: number;
  minWidth?: number;
}

interface StoredColumn {
  value?: string;
  label?: string;
  display?: boolean;
}

/**
 * Hydrate a column-display config from localStorage, merging stored
 * `display` flags onto the canonical defaults. Re-saves on schema drift
 * (column added/removed in code since the user last saved their layout).
 *
 * @param defaults
 * @param storageKey
 */
export function parseStoredTableColumns<T extends ColumnDef>(
  defaults: T[],
  storageKey: string
): T[] {
  if (typeof localStorage === 'undefined') {
    return defaults;
  }
  const raw = localStorage.getItem(storageKey);
  if (!raw) {
    localStorage.setItem(storageKey, JSON.stringify(defaults));
    return defaults;
  }
  try {
    const parsed = JSON.parse(raw) as StoredColumn[];
    if (!Array.isArray(parsed)) {
      throw new Error('Stored columns is not an array');
    }
    const knownValues = new Set(defaults.map(d => d.value));
    const needResave = parsed.some(c => !knownValues.has(c?.value ?? ''));
    const result = defaults.map(column => {
      const stored = parsed.find(p => p?.value === column.value);
      if (stored && stored.value && stored.label) {
        return { ...column, display: stored.display ?? false };
      }
      return column;
    });
    if (needResave) {
      localStorage.setItem(storageKey, JSON.stringify(result));
    }
    return result;
  } catch {
    localStorage.setItem(storageKey, JSON.stringify(defaults));
    return defaults;
  }
}

/**
 * Manages a table's column-display config, hydrated once from localStorage
 * on mount. Returns the columns, the raw setter, and a `toggleColumn`
 * helper that flips one column's visibility and persists the layout
 * (refusing to hide the last visible column so the table never empties).
 *
 * @param defaults
 * @param storageKey
 */
export function useTableColumns<T extends ColumnDef>(
  defaults: T[],
  storageKey: string
): [T[], React.Dispatch<React.SetStateAction<T[]>>, (value: string) => void] {
  const [columns, setColumns] = React.useState<T[]>(defaults);
  const hydrated = React.useRef(false);
  React.useEffect(() => {
    if (hydrated.current) {
      return;
    }
    hydrated.current = true;
    setColumns(parseStoredTableColumns(defaults, storageKey));
    // One-time hydration; defaults/storageKey are expected to be stable.
  }, []);

  const toggleColumn = React.useCallback(
    (value: string) => {
      setColumns(prev => {
        const next = prev.map(column =>
          column.value === value
            ? { ...column, display: column.display === false }
            : column
        );
        if (!next.some(column => column.display !== false)) {
          return prev;
        }
        localStorage.setItem(storageKey, JSON.stringify(next));
        return next;
      });
    },
    [storageKey]
  );

  return [columns, setColumns, toggleColumn];
}
